import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  EquipmentType, Modality, ContractLegStatus, AmendmentChangeType,
  ContractLegQueryFilterLegGrouping,
} from 'api/api';
import { IContractLegsFilter, IAmendmentLegChangesFilter } from 'types/LegsFilter';

interface UseUrlFiltersOptions {
  isAmendment?: boolean;
}

const DEFAULT_LEG_GROUPING = ContractLegQueryFilterLegGrouping.Distinct;

export const parseArrayParam = (param: string | null): string[] | undefined => {
  if (!param) return undefined;
  try {
    return JSON.parse(param);
  } catch {
    return undefined;
  }
};

const parseEnumArrayParam = <T>(
  param: string | null, enumValues: Record<string, T>,
): T[] | undefined => {
  const parsed = parseArrayParam(param);
  if (!parsed) return undefined;
  const validValues = Object.values(enumValues);
  return parsed.filter((value) => validValues.includes(value as T)) as T[];
};

/**
 * Supports both repeated params (?key=a&key=b) and JSON array stored under a single key
 */
const parseEnumParamFlexible = <T>(
  params: URLSearchParams,
  key: string,
  enumValues: Record<string, T>,
): T[] | undefined => {
  const all = params.getAll(key);
  const validValues = Object.values(enumValues);
  if (all.length > 1) {
    return all.filter((value) => validValues.includes(value as T)) as T[];
  }
  const single = params.get(key);
  if (!single) return undefined;
  if (single.trim().startsWith('[')) {
    return parseEnumArrayParam(single, enumValues);
  }
  // If single exists and is valid, return one-element array
  const asValue = validValues.includes(single as T) ? [single as unknown as T] : undefined;
  return asValue as T[] | undefined;
};

export function useUrlFilters({ isAmendment = false }: UseUrlFiltersOptions = {}) {
  const [searchParams, setSearchParams] = useSearchParams();

  const activeFilters = useMemo(() => {
    const baseFilters = {
      originUnlocode: parseArrayParam(searchParams.get('originUnlocode')),
      originUnlocodeGroup: parseArrayParam(searchParams.get('originUnlocodeGroup')),
      destinationUnlocode: parseArrayParam(searchParams.get('destinationUnlocode')),
      destinationUnlocodeGroup: parseArrayParam(searchParams.get('destinationUnlocodeGroup')),
      equipmentType: parseEnumArrayParam(searchParams.get('equipmentType'), EquipmentType),
      status: parseEnumArrayParam(searchParams.get('status'), ContractLegStatus),
      modality: parseEnumArrayParam(searchParams.get('modality'), Modality),
      legGrouping: (searchParams.get('legGrouping') as ContractLegQueryFilterLegGrouping)
        || DEFAULT_LEG_GROUPING,
    };

    if (isAmendment) {
      return {
        ...baseFilters,
        changeType: parseEnumParamFlexible(searchParams, 'changeType', AmendmentChangeType),
        modality: parseEnumArrayParam(searchParams.get('modality'), Modality),
      } as IAmendmentLegChangesFilter;
    }

    return baseFilters as IContractLegsFilter;
  }, [searchParams, isAmendment]);

  useEffect(() => {
    if (searchParams.has('legGrouping')) return;

    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set('legGrouping', DEFAULT_LEG_GROUPING);
      return newParams;
    }, { replace: true });
  }, [searchParams, setSearchParams]);

  const setFilter = useCallback(<K extends keyof (IContractLegsFilter | IAmendmentLegChangesFilter)>
  (
    key: K,
    value: (IContractLegsFilter | IAmendmentLegChangesFilter)[K],
  ) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);

      if (value === undefined || (Array.isArray(value) && value.length === 0)) {
        newParams.delete(key as string);
      } else if (key === 'legGrouping') {
        // legGrouping is a single enum value, not an array, so don't JSON.stringify it
        newParams.set(key as string, value as string);
      } else if ((key as keyof IAmendmentLegChangesFilter) === 'changeType') {
        // Store changeType as repeated params for better UX and interoperability
        newParams.delete('changeType');
        (value as string[]).forEach((v) => newParams.append('changeType', v));
      } else {
        newParams.set(key as string, JSON.stringify(value));
      }

      return newParams;
    }, { replace: true });
  }, [setSearchParams]);

  const clearFilters = useCallback((
    newInitialState?: IContractLegsFilter | IAmendmentLegChangesFilter,
  ) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);

      // Remove all filter-related params
      const filterKeys = [
        'originUnlocode', 'originUnlocodeGroup', 'destinationUnlocode', 'destinationUnlocodeGroup',
        'equipmentType', 'status', 'legGrouping', 'changeType', 'modality',
      ];

      filterKeys.forEach((key) => newParams.delete(key));

      const initialState = newInitialState ?? { legGrouping: DEFAULT_LEG_GROUPING };

      Object.entries(initialState).forEach(([key, value]) => {
        if (value !== undefined && !(Array.isArray(value) && value.length === 0)) {
          if (key === 'legGrouping') {
            // legGrouping is a single enum value, not an array, so don't JSON.stringify it
            newParams.set(key, value as string);
          } else {
            newParams.set(key, JSON.stringify(value));
          }
        }
      });

      return newParams;
    }, { replace: true });
  }, [setSearchParams]);

  return {
    activeFilters,
    setFilter,
    clearFilters,
  };
}
